import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FaBirthdayCake,
    FaStar,
    FaBox,
    FaWhatsapp,
    FaMapMarkerAlt,
} from 'react-icons/fa';
import CakeCard from '../components/CakeCard';
import './Home.css';
import { WHY_US, MARQUEE_ITEMS } from '../constants/data';
import { useSettings } from '../context/SettingsContext';
import { api } from '../lib/api';

// Map icon name strings (from data.js) → react-icons components
const WHY_ICONS = {
    FaBirthdayCake,
    FaStar,
    FaBox,
    FaWhatsapp,
};

const ICON_COLOR = '#6B1A1A';

export default function Home({ addToCart }) {
    const SITE_CONFIG = useSettings();
    const [cakes, setCakes] = useState([]);
    const [active, setActive] = useState('All');
    const [added, setAdded] = useState(null);
    const [heroIndex, setHeroIndex] = useState(0);

    useEffect(() => {
        let mounted = true;
        api.getCakes().then(({ cakes }) => {
            if (mounted) setCakes(cakes);
        });
        return () => { mounted = false; };
    }, []);

    // Hero carousel interval
    useEffect(() => {
        const featuredCount = cakes.filter(c => c.featured).length;
        if (featuredCount <= 1) return;
        
        const interval = setInterval(() => {
            setHeroIndex(prev => prev + 1);
        }, 5000);
        return () => clearInterval(interval);
    }, [cakes]);

    // Categories are derived from whatever cakes the admin has actually added,
    // so a brand-new category typed in the admin form shows up here automatically.
    const categories = useMemo(() => {
        const set = new Set(cakes.map(c => c.category).filter(Boolean));
        return ['All', ...Array.from(set)];
    }, [cakes]);

    const filtered = active === 'All' ? cakes : cakes.filter(c => c.category === active);

    const handleAdd = (cake) => {
        addToCart(cake);
        setAdded(cake.id);
        setTimeout(() => setAdded(null), 1800);
    };

    const featuredCakes = cakes.filter(c => c.featured);
    const currentHeroCakes = featuredCakes.length > 0 ? featuredCakes : (cakes.length > 0 ? [cakes[0]] : []);
    const activeIndex = currentHeroCakes.length > 0 ? heroIndex % currentHeroCakes.length : 0;
    const activeHeroCake = currentHeroCakes[activeIndex];

    return (
        <div className="home">

            {/* ── HERO ── */}
            <section className="hero">
                <div className="hero-overlay" />
                {currentHeroCakes.map((cake, idx) => (
                    <div 
                        key={cake.id || idx}
                        className={`hero-img ${idx === activeIndex ? 'active' : ''}`} 
                        style={{ backgroundImage: `url(${cake.image})` }} 
                    />
                ))}
                
                <div className="hero-content">
                    <p className="hero-eyebrow">{SITE_CONFIG.location}</p>
                    <h1 className="hero-title">{SITE_CONFIG.brandName.toUpperCase()}</h1>
                    <div className="hero-rule" />
                    <p className="hero-sub">{SITE_CONFIG.tagline}</p>
                    <p className="hero-desc">{activeHeroCake?.description || 'Every layer tells your story — crafted with love, baked for you.'}</p>
                    <div className="hero-ctas">
                        {activeHeroCake ? (
                            <Link to={`/cake/${activeHeroCake.id}`} className="btn-primary">View Details</Link>
                        ) : (
                            <Link to="/order" className="btn-primary">View My Order</Link>
                        )}
                        <a href="#gallery" className="btn-outline">Explore Cakes</a>
                    </div>
                </div>
            </section>

            {/* ── MARQUEE STRIP ── */}
            <div className="marquee-strip">
                <div className="marquee-track">
                    {Array(6).fill(null).map((_, i) => (
                        <span key={i}>
                            {MARQUEE_ITEMS.map(item => `✦ ${item} `).join('')}
                        </span>
                    ))}
                </div>
            </div>

            {/* ── GALLERY ── */}
            <section className="gallery" id="gallery">
                <div className="section-head">
                    <p className="section-subtitle">Our Collection</p>
                    <h2 className="section-title">{SITE_CONFIG.slogan}</h2>
                    <div className="gold-rule" style={{ maxWidth: 320, margin: '16px auto 0' }} />
                </div>

                <div className="filter-tabs">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            className={`filter-tab ${active === cat ? 'active' : ''}`}
                            onClick={() => setActive(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div className="cakes-grid">
                    {filtered.map(cake => (
                        <div key={cake.id} className="cake-card-wrap">
                            <CakeCard cake={cake} addToCart={() => handleAdd(cake)} />
                            {added === cake.id && <div className="added-toast">Added to order ✓</div>}
                        </div>
                    ))}
                </div>
            </section>

            {/* ── WHY US ── */}
            <section className="why-us">
                <div className="section-head">
                    <p className="section-subtitle">Why {SITE_CONFIG.brandName}</p>
                    <h2 className="section-title">The {SITE_CONFIG.brandName} Promise</h2>
                    <div className="gold-rule" style={{ maxWidth: 320, margin: '16px auto 0' }} />
                </div>
                <div className="why-grid">
                    {WHY_US.map((item, i) => {
                        const Icon = WHY_ICONS[item.icon];
                        return (
                            <div className="why-card" key={i}>
                                {Icon && (
                                    <span className="why-icon">
                                        <Icon size={32} color={ICON_COLOR} />
                                    </span>
                                )}
                                <h3 className="why-title">{item.title}</h3>
                                <p className="why-desc">{item.description}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ── CTA BANNER ── */}
            <section className="cta-banner">
                <div className="cta-inner">
                    <p className="section-subtitle" style={{ color: 'var(--gold-soft)' }}>Ready to Order?</p>
                    <h2 className="cta-title">Design Your Dream Cake</h2>
                    <div className="hero-rule" />
                    <p className="cta-desc">Reach out on WhatsApp or fill our order form — we'll bring your vision to life.</p>
                    <div className="cta-btns">
                        <a
                            href={`https://wa.me/${SITE_CONFIG.whatsapp}?text=Hello%20${SITE_CONFIG.brandName}!%20I%20want%20to%20order%20a%20cake.`}
                            target="_blank" rel="noopener noreferrer"
                            className="btn-gold"
                        >
                            WhatsApp Us
                        </a>
                        <Link to="/contact" className="btn-outline-light">Contact Us</Link>
                    </div>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="footer">
                <div className="footer-inner">
                    <div className="footer-logo">
                        <span className="logo-word">{SITE_CONFIG.brandName.toUpperCase()}</span>
                        <span className="logo-tag">{SITE_CONFIG.tagline}</span>
                    </div>
                    <div className="gold-rule" style={{ maxWidth: 280, margin: '0 auto' }} />
                    <div className="footer-links">
                        <Link to="/">Home</Link>
                        <Link to="/order">Order</Link>
                        <Link to="/contact">Contact</Link>
                    </div>
                    <p className="footer-location">
                        <FaMapMarkerAlt
                            size={13}
                            color={ICON_COLOR}
                            style={{ verticalAlign: 'middle', marginRight: 5 }}
                        />
                        {SITE_CONFIG.location} &nbsp;·&nbsp; {SITE_CONFIG.whatsappDisplay}
                    </p>
                    <p className="footer-copy">© {SITE_CONFIG.established} {SITE_CONFIG.brandName}. All rights reserved.</p>
                </div>
            </footer>

        </div>
    );
}
